import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { SharedService } from '../../services/shared.service';
import { InvoiceService } from '../../services/invoice.service';
import { PaymentLinksService } from '../../services/payment-links.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PatientService } from '../../services/patient.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-invoice-profile',
  templateUrl: './invoice-profile.component.html',
  styleUrls: ['./invoice-profile.component.css']
})
export class InvoiceProfileComponent implements OnInit, AfterViewInit {
  @ViewChild('patientNameInput', { static: false }) patientNameInput: ElementRef;
  showErrorMessage: boolean;
  errorMessage: string;
  showLoadingMessage = false;

  /** accounts | payments */
  activeFinanceTab = 'accounts';

  /** Full list from API (active + inactive), each item has isActive */
  allPatientDtos: any[] = [];
  residentName = '';
  /** Client-side filter: substring match on patient id (numeric id as string) */
  patientIdFilter = '';
  /** When true: show only inactive; when false: show only active */
  showOnlyInactive = false;

  /** Payments tab: selected patient ids for bulk email / SMS */
  paymentSelectedMap: { [patientId: number]: boolean } = {};

  /** Last successful payment-setup-link email (Toronto local), keyed by patientId */
  paymentLastEmailSentMap: { [patientId: number]: string } = {};

  /** Last successful payment-setup-link SMS (Toronto local), keyed by patientId */
  paymentLastSmsSentMap: { [patientId: number]: string } = {};

  /** Card / bank counts from SolvoPayment, keyed by patientId */
  paymentMethodsSummaryMap: {
    [patientId: number]: { found: boolean; cardCount: number; bankAccountCount: number }
  } = {};
  paymentMethodsSummaryLoading = false;

  constructor(
    private sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute,
    private patientService: PatientService,
    private invoiceService: InvoiceService,
    private paymentLinksService: PaymentLinksService
  ) {}

  ngOnInit(): void {
    this.sharedService.reloadUserPrivileges();
    this.restoreFinanceTabFromQuery();
    const self = this;
    this.route.queryParams.subscribe(function (qp) {
      const tabId = self.getFinanceTabIdFromQuery(qp['tab']);
      if (tabId) {
        self.activeFinanceTab = tabId;
      }
    });
    this.loadAllPatientDtos();
  }

  ngAfterViewInit(): void {
    if (this.patientNameInput && this.patientNameInput.nativeElement) {
      this.patientNameInput.nativeElement.focus();
    }
  }

  selectFinanceTab(tabId: string): void {
    if (tabId !== 'accounts' && tabId !== 'payments') {
      return;
    }
    this.activeFinanceTab = tabId;
    this.syncFinanceTabQueryParam(tabId);
  }

  private restoreFinanceTabFromQuery(): void {
    const tabId = this.getFinanceTabIdFromQuery(this.route.snapshot.queryParamMap.get('tab'));
    if (tabId) {
      this.activeFinanceTab = tabId;
    }
  }

  private getFinanceTabIdFromQuery(queryValue: string): string | null {
    if (!queryValue) {
      return null;
    }
    const normalized = String(queryValue).trim().toLowerCase();
    if (normalized === 'payments') {
      return 'payments';
    }
    if (normalized === 'accounts') {
      return 'accounts';
    }
    return null;
  }

  private syncFinanceTabQueryParam(tabId: string): void {
    const queryParams: any = {
      tab: tabId === 'payments' ? 'payments' : null
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /** Load active and inactive lists once, merge with isActive flag */
  loadAllPatientDtos(): void {
    this.showLoadingMessage = true;
    const active$ = this.patientService.getPatientDtos(
      this.sharedService.pharmacyId,
      this.sharedService.facilityId,
      this.sharedService.getUserId(),
      true
    );
    const inactive$ = this.patientService.getPatientDtos(
      this.sharedService.pharmacyId,
      this.sharedService.facilityId,
      this.sharedService.getUserId(),
      false
    );
    forkJoin([active$, inactive$]).subscribe({
      next: ([activeList, inactiveList]) => {
        activeList.forEach((p: any) => (p.isActive = true));
        inactiveList.forEach((p: any) => (p.isActive = false));
        this.allPatientDtos = [...(activeList || []), ...(inactiveList || [])];
        this.paymentSelectedMap = {};
        this.showLoadingMessage = false;
        this.loadPaymentSetupLinkEmailLastSent();
        this.loadPaymentSetupLinkSmsLastSent();
        this.loadPaymentMethodsSummaries();
      },
      error: () => (this.showLoadingMessage = false)
    });
  }

  loadPaymentMethodsSummaries(): void {
    const self = this;
    const ids: number[] = [];
    for (let i = 0; i < this.allPatientDtos.length; i++) {
      const id = this.allPatientDtos[i] && this.allPatientDtos[i].id;
      if (id != null && !isNaN(Number(id))) {
        ids.push(Number(id));
      }
    }
    if (ids.length === 0) {
      this.paymentMethodsSummaryMap = {};
      return;
    }

    this.paymentMethodsSummaryLoading = true;
    this.invoiceService.queryPatientPaymentMethodsSummary({
      pharmacyId: this.sharedService.pharmacyId,
      patientIds: ids
    }).subscribe(
      function (result) {
        self.paymentMethodsSummaryLoading = false;
        const map: {
          [patientId: number]: { found: boolean; cardCount: number; bankAccountCount: number }
        } = {};
        const items = result && result.success && result.data ? result.data : [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item && item.patientId != null) {
            map[item.patientId] = {
              found: !!item.found,
              cardCount: item.cardCount != null ? Number(item.cardCount) : 0,
              bankAccountCount: item.bankAccountCount != null ? Number(item.bankAccountCount) : 0
            };
          }
        }
        self.paymentMethodsSummaryMap = map;
      },
      function () {
        self.paymentMethodsSummaryLoading = false;
        self.paymentMethodsSummaryMap = {};
      }
    );
  }

  loadPaymentSetupLinkEmailLastSent(): void {
    const self = this;
    const ids: number[] = [];
    for (let i = 0; i < this.allPatientDtos.length; i++) {
      const id = this.allPatientDtos[i] && this.allPatientDtos[i].id;
      if (id != null && !isNaN(Number(id))) {
        ids.push(Number(id));
      }
    }
    if (ids.length === 0) {
      this.paymentLastEmailSentMap = {};
      return;
    }

    this.invoiceService.getPaymentSetupLinkEmailLastSent({
      pharmacyId: this.sharedService.pharmacyId,
      facilityId: this.sharedService.facilityId,
      patientIds: ids
    }).subscribe(
      function (result) {
        const map: { [patientId: number]: string } = {};
        const items = result && result.success && result.data ? result.data : [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item && item.patientId != null && item.lastEmailSentAt) {
            map[item.patientId] = String(item.lastEmailSentAt);
          }
        }
        self.paymentLastEmailSentMap = map;
      },
      function () {
        self.paymentLastEmailSentMap = {};
      }
    );
  }

  loadPaymentSetupLinkSmsLastSent(): void {
    const self = this;
    const ids: number[] = [];
    for (let i = 0; i < this.allPatientDtos.length; i++) {
      const id = this.allPatientDtos[i] && this.allPatientDtos[i].id;
      if (id != null && !isNaN(Number(id))) {
        ids.push(Number(id));
      }
    }
    if (ids.length === 0) {
      this.paymentLastSmsSentMap = {};
      return;
    }

    this.invoiceService.getPaymentSetupLinkSmsLastSent({
      pharmacyId: this.sharedService.pharmacyId,
      facilityId: this.sharedService.facilityId,
      patientIds: ids
    }).subscribe(
      function (result) {
        const map: { [patientId: number]: string } = {};
        const items = result && result.success && result.data ? result.data : [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item && item.patientId != null && item.lastSmsSentAt) {
            map[item.patientId] = String(item.lastSmsSentAt);
          }
        }
        self.paymentLastSmsSentMap = map;
      },
      function () {
        self.paymentLastSmsSentMap = {};
      }
    );
  }

  /** Client-side filtered list: inactive/active, Patient Name, Patient ID (substring on id) */
  get filteredPatientDtos(): any[] {
    if (!this.allPatientDtos || this.allPatientDtos.length === 0) {
      return [];
    }
    let list = this.showOnlyInactive
      ? this.allPatientDtos.filter((p) => !p.isActive)
      : this.allPatientDtos.filter((p) => p.isActive);
    const term = (this.residentName || '').trim().toLowerCase();
    if (term) {
      list = list.filter((p) => {
        const fullName = ((p.lastName || '') + ' ' + (p.firstName || '')).toLowerCase();
        const fullNameReversed = ((p.firstName || '') + ' ' + (p.lastName || '')).toLowerCase();
        return fullName.indexOf(term) !== -1 || fullNameReversed.indexOf(term) !== -1;
      });
    }
    const idTerm = (this.patientIdFilter || '').trim();
    if (idTerm) {
      list = list.filter((p) => {
        const idStr = p.id != null ? String(p.id) : '';
        return idStr.indexOf(idTerm) !== -1;
      });
    }
    return list;
  }

  isPaymentSelected(patientId: number): boolean {
    return !!this.paymentSelectedMap[patientId];
  }

  togglePaymentSelected(patientId: number, checked: boolean): void {
    if (checked) {
      this.paymentSelectedMap[patientId] = true;
    } else {
      delete this.paymentSelectedMap[patientId];
    }
  }

  onPaymentCheckboxChange(patientId: number, event: any): void {
    const checked = !!(event && event.target && event.target.checked);
    this.togglePaymentSelected(patientId, checked);
  }

  get selectedPaymentCount(): number {
    return Object.keys(this.paymentSelectedMap).length;
  }

  get allVisiblePaymentsSelected(): boolean {
    const list = this.filteredPatientDtos;
    if (!list || list.length === 0) {
      return false;
    }
    for (let i = 0; i < list.length; i++) {
      if (!this.isPaymentSelected(list[i].id)) {
        return false;
      }
    }
    return true;
  }

  toggleSelectAllPayments(checked: boolean): void {
    const list = this.filteredPatientDtos;
    for (let i = 0; i < list.length; i++) {
      this.togglePaymentSelected(list[i].id, checked);
    }
  }

  onSelectAllPaymentsChange(event: any): void {
    const checked = !!(event && event.target && event.target.checked);
    this.toggleSelectAllPayments(checked);
  }

  onPaymentCheckCellClick(event: any, patientId: number): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.togglePaymentSelected(patientId, !this.isPaymentSelected(patientId));
  }

  /**
   * Card count for Payments tab. Number only when registered; empty when none.
   */
  getPaymentCardCountDisplay(patient: any): string {
    if (!patient || patient.id == null) {
      return '';
    }
    if (this.paymentMethodsSummaryLoading && !this.paymentMethodsSummaryMap[patient.id]) {
      return '';
    }
    const summary = this.paymentMethodsSummaryMap[patient.id];
    if (!summary) {
      return '';
    }
    const cards = summary.cardCount != null ? summary.cardCount : 0;
    return cards > 0 ? String(cards) : '';
  }

  /**
   * Bank account count for Payments tab. Number only when registered; empty when none.
   */
  getPaymentBankCountDisplay(patient: any): string {
    if (!patient || patient.id == null) {
      return '';
    }
    if (this.paymentMethodsSummaryLoading && !this.paymentMethodsSummaryMap[patient.id]) {
      return '';
    }
    const summary = this.paymentMethodsSummaryMap[patient.id];
    if (!summary) {
      return '';
    }
    const banks = summary.bankAccountCount != null ? summary.bankAccountCount : 0;
    return banks > 0 ? String(banks) : '';
  }

  /**
   * Last successful payment setup link email from UserActionLog (or —).
   */
  getLastEmailSentText(patient: any): string {
    if (!patient || patient.id == null) {
      return '—';
    }
    return this.formatLastSentText(this.paymentLastEmailSentMap[patient.id]);
  }

  /**
   * Last successful payment setup link SMS from UserActionLog (or —).
   */
  getLastSmsSentText(patient: any): string {
    if (!patient || patient.id == null) {
      return '—';
    }
    return this.formatLastSentText(this.paymentLastSmsSentMap[patient.id]);
  }

  private formatLastSentText(raw: string): string {
    if (!raw) {
      return '—';
    }
    const text = String(raw);
    // Stored as Toronto local; prefer string slice to avoid browser TZ shifts.
    if (text.indexOf('T') > 0 && text.length >= 16) {
      return text.substring(0, 10) + ' ' + text.substring(11, 16);
    }
    const d = new Date(text);
    if (isNaN(d.getTime())) {
      return '—';
    }
    const pad = function (n: number) {
      return n < 10 ? '0' + n : String(n);
    };
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
      + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
  }

  emailSelectedPaymentSetupLinks(): void {
    const patientIds = this.getSelectedPaymentPatientIds();
    if (!patientIds || patientIds.length === 0) {
      return;
    }

    const self = this;
    Swal.fire({
      title: 'Preparing...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      onOpen: function () {
        Swal.showLoading();
      }
    });

    this.invoiceService.getPaymentVerifyLinkEmailBatchOptions({
      pharmacyId: this.sharedService.pharmacyId,
      facilityId: this.sharedService.facilityId,
      patientIds: patientIds
    }).subscribe(
      function (result) {
        if (!result || !result.success || !result.data) {
          const msg = (result && result.data) ? String(result.data) : 'Failed to load email recipients.';
          Swal.fire({ icon: 'error', title: 'Unable to prepare email', text: msg, confirmButtonText: 'OK' });
          return;
        }

        const recipients = result.data.recipients || [];
        const withAccess = recipients.filter(function (r: any) { return !!r.canSend; });
        const isProduction = !!result.data.isProduction;
        const envNote = !isProduction
          ? '<div class="text-danger small mb-2">This is not production. Patient email can only be selected in the production environment. Please use Custom email.</div>'
          : '';

        if (withAccess.length === 0) {
          Swal.fire({
            icon: 'warning',
            title: 'No recipients ready',
            html: envNote
              + self.buildRecipientPreviewHtml(recipients, isProduction, false)
              + '<p class="mt-2 mb-0 text-muted">Fix missing access keys, then try again.</p>',
            width: 900,
            confirmButtonText: 'OK'
          });
          return;
        }

        const subjectDefault = result.data.emailSubject
          || 'Please set up your payment method — Advantage Care Pharmacy';
        const bodyPreview = (result.data.emailBody || '')
          .replace(/\{\{PAYMENT_REGISTER_URL\}\}/g, '#');

        Swal.fire({
          icon: 'info',
          iconHtml: self.swalEnvelopeIconHtml(),
          title: 'Send Email Setup Link?',
          width: 920,
          html: `
            <div style="text-align:left;">
              <p class="text-muted mb-2">
                Choose Patient email or a custom address for each patient.
                A secure payment method setup link will be emailed to each ready recipient.
                Emails are sent from solvoit.notification@gmail.com.
              </p>
              ${envNote}
              <div class="mb-2 fw-bold">Recipients <span id="payment-email-batch-count"></span></div>
              ${self.buildRecipientPreviewHtml(recipients, isProduction, true)}
              <hr class="my-2" />
              <div class="mb-2 fw-bold">Email content (preview)</div>
              <div style="border:1px solid #dee2e6;border-radius:6px;padding:12px 14px;background:#f8f9fa;">
                <div style="font-size:12px;color:#6c757d;margin-bottom:4px;">Subject</div>
                <div style="font-weight:600;margin-bottom:12px;">${self.escapeHtml(subjectDefault)}</div>
                <div style="font-size:12px;color:#6c757d;margin-bottom:4px;">Body</div>
                <div style="background:#fff;border:1px solid #e9ecef;border-radius:4px;padding:12px;max-height:280px;overflow:auto;">
                  ${bodyPreview}
                </div>
                <div class="text-muted mt-2" style="font-size:12px;">
                  Name placeholders: {FirstName} {LastName}. Each email replaces them with that patient&apos;s name,
                  and the blue button becomes a unique setup link.
                </div>
              </div>
            </div>
          `,
          showCancelButton: true,
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Send',
          onOpen: function () {
            self.sizeSwalCustomIcon();
            const countEl = document.getElementById('payment-email-batch-count');

            const selectedMode = function (index: number) {
              const patientRadio = document.getElementById('payment-email-batch-patient-' + index) as HTMLInputElement;
              if (patientRadio && patientRadio.checked) {
                return 'patient';
              }
              return 'custom';
            };

            const readRowState = function (index: number) {
              const r = recipients[index];
              const resultState = {
                ok: false,
                reason: r && r.skipReason ? String(r.skipReason) : 'Skipped'
              };
              if (!r || !r.canSend) {
                return resultState;
              }

              const mode = selectedMode(index);
              if (mode === 'patient') {
                resultState.ok = !!r.allowPatientEmail;
                if (!resultState.ok) {
                  resultState.reason = 'Patient email is missing or invalid.';
                }
                return resultState;
              }

              const customInput = document.getElementById('payment-email-batch-custom-input-' + index) as HTMLInputElement;
              const customVal = customInput && customInput.value ? String(customInput.value).trim() : '';
              if (!customVal) {
                resultState.reason = 'Enter a custom email address.';
                return resultState;
              }
              if (!self.isValidEmail(customVal)) {
                resultState.reason = 'Custom email is invalid.';
                return resultState;
              }
              resultState.ok = true;
              return resultState;
            };

            const updateStatuses = function () {
              let ready = 0;
              let skipped = 0;
              for (let i = 0; i < recipients.length; i++) {
                const state = readRowState(i);
                if (state.ok) {
                  ready++;
                } else {
                  skipped++;
                }
                const statusEl = document.getElementById('payment-email-batch-status-' + i);
                if (statusEl) {
                  statusEl.innerHTML = state.ok
                    ? '<span class="text-success">Ready</span>'
                    : '<span class="text-danger">' + self.escapeHtml(state.reason) + '</span>';
                }
              }
              if (countEl) {
                countEl.textContent = '(' + ready + ' will be sent'
                  + (skipped > 0 ? ', ' + skipped + ' skipped' : '')
                  + ')';
              }
              const confirmBtn = document.querySelector('.swal2-confirm') as HTMLElement;
              if (confirmBtn) {
                confirmBtn.textContent = 'Send (' + ready + ')';
              }
            };

            const bindRow = function (index: number) {
              const patientRadio = document.getElementById('payment-email-batch-patient-' + index) as HTMLInputElement;
              const customRadio = document.getElementById('payment-email-batch-custom-' + index) as HTMLInputElement;
              const customInput = document.getElementById('payment-email-batch-custom-input-' + index) as HTMLInputElement;

              const selectCustom = function () {
                if (customRadio) {
                  customRadio.checked = true;
                }
                updateStatuses();
              };

              const syncCustomEmailInput = function () {
                if (!customInput) {
                  return;
                }
                const customVal = customInput.value ? String(customInput.value).trim() : '';
                if (customVal && customRadio) {
                  customRadio.checked = true;
                }
                const isInvalid = customVal.length > 0 && !self.isValidEmail(customVal);
                if (isInvalid) {
                  customInput.classList.add('is-invalid');
                } else {
                  customInput.classList.remove('is-invalid');
                }
                updateStatuses();
              };

              if (patientRadio) {
                patientRadio.addEventListener('change', updateStatuses);
              }
              if (customRadio) {
                customRadio.addEventListener('change', updateStatuses);
              }
              if (customInput) {
                customInput.addEventListener('focus', selectCustom);
                customInput.addEventListener('click', selectCustom);
                customInput.addEventListener('input', syncCustomEmailInput);
                customInput.addEventListener('blur', syncCustomEmailInput);
              }
            };

            for (let i = 0; i < recipients.length; i++) {
              bindRow(i);
            }
            updateStatuses();
          },
          preConfirm: function () {
            const sendable: any[] = [];
            for (let i = 0; i < recipients.length; i++) {
              const r = recipients[i];
              if (!r || !r.canSend) {
                continue;
              }

              const patientRadio = document.getElementById('payment-email-batch-patient-' + i) as any;
              const customInput = document.getElementById('payment-email-batch-custom-input-' + i) as any;
              const displayName = r.listDisplayName || ((r.lastName || '') + ', ' + (r.firstName || ''));
              const mode = (patientRadio && patientRadio.checked) ? 'patient' : 'custom';

              if (mode === 'patient') {
                if (!isProduction || !r.allowPatientEmail) {
                  continue;
                }
                sendable.push({
                  patientId: r.patientId,
                  listDisplayName: displayName,
                  firstName: r.firstName,
                  lastName: r.lastName,
                  accessKey: r.accessKey,
                  recipientMode: 'patient',
                  customEmail: null
                });
                continue;
              }

              const customVal = customInput && customInput.value ? String(customInput.value).trim() : '';
              if (!customVal) {
                continue;
              }
              if (!self.isValidEmail(customVal)) {
                Swal.showValidationMessage(displayName + ': please enter a valid email address.');
                return null;
              }
              sendable.push({
                patientId: r.patientId,
                listDisplayName: displayName,
                firstName: r.firstName,
                lastName: r.lastName,
                accessKey: r.accessKey,
                recipientMode: 'custom',
                customEmail: customVal
              });
            }

            if (sendable.length === 0) {
              Swal.showValidationMessage('No recipients are ready to send.');
              return null;
            }

            return { recipients: sendable };
          }
        }).then(function (swalResult) {
          if (!swalResult || !swalResult.isConfirmed || !swalResult.value) {
            return;
          }
          self.sendSetupLinkEmailsSequentially(swalResult.value.recipients);
        });
      },
      function (err) {
        const msg = err && err.error && err.error.data
          ? String(err.error.data)
          : 'Failed to load email recipients.';
        Swal.fire({ icon: 'error', title: 'Unable to prepare email', text: msg, confirmButtonText: 'OK' });
      }
    );
  }

  smsSelectedPaymentSetupLinks(): void {
    const patientIds = this.getSelectedPaymentPatientIds();
    if (!patientIds || patientIds.length === 0) {
      return;
    }

    const self = this;
    Swal.fire({
      title: 'Preparing...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      onOpen: function () {
        Swal.showLoading();
      }
    });

    this.invoiceService.getPaymentVerifyLinkSmsBatchOptions({
      pharmacyId: this.sharedService.pharmacyId,
      facilityId: this.sharedService.facilityId,
      patientIds: patientIds
    }).subscribe(
      function (result) {
        if (!result || !result.success || !result.data) {
          const msg = (result && result.data) ? String(result.data) : 'Failed to load SMS recipients.';
          Swal.fire({ icon: 'error', title: 'Unable to prepare SMS', text: msg, confirmButtonText: 'OK' });
          return;
        }

        const recipients = result.data.recipients || [];
        const withAccess = recipients.filter(function (r: any) { return !!r.canSend; });
        const isProduction = !!result.data.isProduction;
        const maxLength = result.data.smsMaxLength > 0 ? result.data.smsMaxLength : 160;
        const smsPreview = self.escapeHtml(result.data.smsBody || '');
        const envNote = !isProduction
          ? '<div class="text-danger small mb-2">This is not production. Phone 1 and Phone 2 can only be selected in the production environment. Please use Custom phone.</div>'
          : '';

        if (withAccess.length === 0) {
          Swal.fire({
            icon: 'warning',
            title: 'No recipients ready',
            html: envNote
              + self.buildSmsRecipientPreviewHtml(recipients, isProduction, false)
              + '<p class="mt-2 mb-0 text-muted">Fix missing access keys, then try again.</p>',
            width: 900,
            confirmButtonText: 'OK'
          });
          return;
        }

        Swal.fire({
          icon: 'success',
          iconHtml: self.swalChatIconHtml(),
          title: 'Send SMS Setup Link?',
          width: 980,
          html: `
            <div style="text-align:left;">
              <p class="text-muted mb-2">
                Choose Phone 1, Phone 2, or a custom number for each patient.
                A short payment method setup link will be sent by SMS to each ready recipient.
              </p>
              ${envNote}
              <div class="mb-2 fw-bold">Recipients <span id="payment-sms-batch-count"></span></div>
              ${self.buildSmsRecipientPreviewHtml(recipients, isProduction, true)}
              <div class="small mt-2">
                <strong>Canadian (+1) numbers only.</strong>
                You can enter (905) 555-1234, 905-555-1234, or 9055551234.
              </div>
              <hr class="my-2" />
              <div class="mb-2 fw-bold">SMS message</div>
              <div style="border:1px solid #dee2e6;border-radius:6px;padding:12px 14px;background:#f8f9fa;">
                <div style="background:#fff;border:1px solid #e9ecef;border-radius:4px;padding:12px;white-space:pre-wrap;font-family:Consolas,Monaco,monospace;font-size:14px;">${smsPreview}</div>
                <div class="text-muted mt-2" style="font-size:12px;">
                  The short URL slot is reserved at 35 characters and is filled when each message is sent.
                  One SMS is ${maxLength} characters.
                </div>
              </div>
            </div>
          `,
          showCancelButton: true,
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Send',
          onOpen: function () {
            self.sizeSwalCustomIcon();
            const countEl = document.getElementById('payment-sms-batch-count');

            const selectedMode = function (index: number) {
              const phone1Radio = document.getElementById('payment-sms-batch-phone1-' + index) as HTMLInputElement;
              const phone2Radio = document.getElementById('payment-sms-batch-phone2-' + index) as HTMLInputElement;
              if (phone1Radio && phone1Radio.checked) {
                return 'phone1';
              }
              if (phone2Radio && phone2Radio.checked) {
                return 'phone2';
              }
              return 'custom';
            };

            const readRowState = function (index: number) {
              const r = recipients[index];
              const resultState = {
                ok: false,
                reason: r && r.skipReason ? String(r.skipReason) : 'Skipped',
                mode: '',
                customPhone: ''
              };
              if (!r || !r.canSend) {
                return resultState;
              }

              const mode = selectedMode(index);
              resultState.mode = mode;
              if (mode === 'phone1') {
                resultState.ok = !!r.allowPhone1;
                if (!resultState.ok) {
                  resultState.reason = 'Phone 1 is missing or invalid.';
                }
                return resultState;
              }
              if (mode === 'phone2') {
                resultState.ok = !!r.allowPhone2;
                if (!resultState.ok) {
                  resultState.reason = 'Phone 2 is missing or invalid.';
                }
                return resultState;
              }

              const customInput = document.getElementById('payment-sms-batch-custom-input-' + index) as HTMLInputElement;
              const customVal = customInput && customInput.value ? String(customInput.value).trim() : '';
              if (!customVal) {
                resultState.reason = 'Enter a custom phone number.';
                return resultState;
              }
              const customPhone = self.normalizeCustomNationalPhoneToE164(customVal);
              if (!customPhone) {
                resultState.reason = 'Custom phone is invalid.';
                return resultState;
              }
              resultState.ok = true;
              resultState.customPhone = customPhone;
              return resultState;
            };

            const updateStatuses = function () {
              let ready = 0;
              let skipped = 0;
              for (let i = 0; i < recipients.length; i++) {
                const state = readRowState(i);
                if (state.ok) {
                  ready++;
                } else {
                  skipped++;
                }
                const statusEl = document.getElementById('payment-sms-batch-status-' + i);
                if (statusEl) {
                  statusEl.innerHTML = state.ok
                    ? '<span class="text-success">Ready</span>'
                    : '<span class="text-danger">' + self.escapeHtml(state.reason) + '</span>';
                }
              }
              if (countEl) {
                countEl.textContent = '(' + ready + ' will be sent'
                  + (skipped > 0 ? ', ' + skipped + ' skipped' : '')
                  + ')';
              }
              const confirmBtn = document.querySelector('.swal2-confirm') as HTMLElement;
              if (confirmBtn) {
                confirmBtn.textContent = 'Send (' + ready + ')';
              }
            };

            const bindRow = function (index: number) {
              const phone1Radio = document.getElementById('payment-sms-batch-phone1-' + index) as HTMLInputElement;
              const phone2Radio = document.getElementById('payment-sms-batch-phone2-' + index) as HTMLInputElement;
              const customRadio = document.getElementById('payment-sms-batch-custom-' + index) as HTMLInputElement;
              const customInput = document.getElementById('payment-sms-batch-custom-input-' + index) as HTMLInputElement;

              const selectCustom = function () {
                if (customRadio) {
                  customRadio.checked = true;
                }
                updateStatuses();
              };

              const syncCustomPhoneInput = function (showError: boolean) {
                if (!customInput) {
                  return;
                }
                var cleaned = self.sanitizeNationalPhoneInput(customInput.value);
                if (customInput.value !== cleaned) {
                  customInput.value = cleaned;
                }
                var digits = cleaned.replace(/\D/g, '');
                if (digits && customRadio) {
                  customRadio.checked = true;
                }
                const isInvalid = !!showError && digits.length > 0
                  && !self.normalizeCustomNationalPhoneToE164(cleaned);
                if (isInvalid) {
                  customInput.classList.add('is-invalid');
                } else {
                  customInput.classList.remove('is-invalid');
                }
                updateStatuses();
              };

              if (phone1Radio) {
                phone1Radio.addEventListener('change', updateStatuses);
              }
              if (phone2Radio) {
                phone2Radio.addEventListener('change', updateStatuses);
              }
              if (customRadio) {
                customRadio.addEventListener('change', updateStatuses);
              }
              if (customInput) {
                customInput.addEventListener('focus', selectCustom);
                customInput.addEventListener('click', selectCustom);
                customInput.addEventListener('input', function () {
                  syncCustomPhoneInput(false);
                });
                customInput.addEventListener('blur', function () {
                  syncCustomPhoneInput(true);
                });
              }
            };

            for (let i = 0; i < recipients.length; i++) {
              bindRow(i);
            }
            updateStatuses();
          },
          preConfirm: function () {
            const sendable: any[] = [];
            for (let i = 0; i < recipients.length; i++) {
              const r = recipients[i];
              if (!r || !r.canSend) {
                continue;
              }

              const phone1Radio = document.getElementById('payment-sms-batch-phone1-' + i) as any;
              const phone2Radio = document.getElementById('payment-sms-batch-phone2-' + i) as any;
              const customInput = document.getElementById('payment-sms-batch-custom-input-' + i) as any;
              let mode = 'custom';
              if (phone1Radio && phone1Radio.checked) {
                mode = 'phone1';
              } else if (phone2Radio && phone2Radio.checked) {
                mode = 'phone2';
              }

              const displayName = r.listDisplayName || ((r.lastName || '') + ', ' + (r.firstName || ''));
              if (mode === 'phone1') {
                if (!isProduction || !r.allowPhone1) {
                  continue;
                }
                sendable.push({
                  patientId: r.patientId,
                  listDisplayName: displayName,
                  firstName: r.firstName,
                  lastName: r.lastName,
                  accessKey: r.accessKey,
                  recipientMode: 'phone1',
                  customPhone: null
                });
                continue;
              }

              if (mode === 'phone2') {
                if (!isProduction || !r.allowPhone2) {
                  continue;
                }
                sendable.push({
                  patientId: r.patientId,
                  listDisplayName: displayName,
                  firstName: r.firstName,
                  lastName: r.lastName,
                  accessKey: r.accessKey,
                  recipientMode: 'phone2',
                  customPhone: null
                });
                continue;
              }

              const customVal = customInput && customInput.value ? String(customInput.value).trim() : '';
              if (!customVal) {
                continue;
              }
              const customPhone = self.normalizeCustomNationalPhoneToE164(customVal);
              if (!customPhone) {
                Swal.showValidationMessage(
                  displayName + ': enter a valid phone number, for example (905) 555-1234, 905-555-1234, or 9055551234.');
                return null;
              }
              sendable.push({
                patientId: r.patientId,
                listDisplayName: displayName,
                firstName: r.firstName,
                lastName: r.lastName,
                accessKey: r.accessKey,
                recipientMode: 'custom',
                customPhone: customPhone
              });
            }

            if (sendable.length === 0) {
              Swal.showValidationMessage('No recipients are ready to send.');
              return null;
            }

            return {
              recipients: sendable,
              smsBody: result.data.smsBody || ''
            };
          }
        }).then(function (swalResult) {
          if (!swalResult || !swalResult.isConfirmed || !swalResult.value) {
            return;
          }
          self.sendSetupLinkSmsSequentially(
            swalResult.value.recipients,
            swalResult.value.smsBody);
        });
      },
      function (err) {
        const msg = err && err.error && err.error.data
          ? String(err.error.data)
          : 'Failed to load SMS recipients.';
        Swal.fire({ icon: 'error', title: 'Unable to prepare SMS', text: msg, confirmButtonText: 'OK' });
      }
    );
  }

  private getSelectedPaymentPatientIds(): number[] {
    const ids: number[] = [];
    const keys = Object.keys(this.paymentSelectedMap);
    for (let i = 0; i < keys.length; i++) {
      const id = Number(keys[i]);
      if (!isNaN(id) && this.paymentSelectedMap[id]) {
        ids.push(id);
      }
    }
    return ids;
  }

  private buildRecipientPreviewHtml(
    recipients: any[],
    isProduction: boolean,
    interactive: boolean): string {
    if (!recipients || recipients.length === 0) {
      return '<p class="text-muted mb-0">No recipients.</p>';
    }
    let rows = '';
    for (let i = 0; i < recipients.length; i++) {
      const r = recipients[i];
      const name = this.escapeHtml(r.listDisplayName || ((r.lastName || '') + ', ' + (r.firstName || '')));
      const email = r.patientEmail
        ? this.escapeHtml(r.patientEmail)
        : '<span class="text-muted">(not available)</span>';
      const status = r.canSend
        ? '<span class="text-success">Ready</span>'
        : '<span class="text-danger">' + this.escapeHtml(r.skipReason || 'Skipped') + '</span>';

      if (!interactive || !r.canSend) {
        rows += '<tr>'
          + '<td style="padding:6px 8px;vertical-align:middle;">' + name + '</td>'
          + '<td style="padding:6px 8px;vertical-align:middle;">' + email + '</td>'
          + '<td style="padding:6px 8px;vertical-align:middle;">'
          + '<span class="text-muted">—</span></td>'
          + '<td id="payment-email-batch-status-' + i + '" style="padding:6px 8px;vertical-align:middle;">'
          + status + '</td>'
          + '</tr>';
        continue;
      }

      const allowPatientEmail = !!isProduction && !!r.allowPatientEmail;
      const patientChecked = allowPatientEmail ? 'checked' : '';
      const customChecked = allowPatientEmail ? '' : 'checked';
      const patientDisabled = allowPatientEmail ? '' : 'disabled';
      const groupName = 'payment-email-batch-mode-' + i;

      rows += '<tr>'
        + '<td style="padding:6px 8px;vertical-align:middle;">' + name + '</td>'
        + '<td style="padding:6px 8px;vertical-align:middle;">'
        + '<label class="mb-0 d-flex align-items-center">'
        + '<input class="form-check-input m-0" type="radio" name="' + groupName + '"'
        + ' id="payment-email-batch-patient-' + i + '" value="patient" '
        + patientChecked + ' ' + patientDisabled + '>'
        + '<span>' + email + '</span></label></td>'
        + '<td style="padding:6px 8px;vertical-align:middle;">'
        + '<div class="d-flex align-items-center">'
        + '<input class="form-check-input m-0" type="radio" name="' + groupName + '"'
        + ' id="payment-email-batch-custom-' + i + '" value="custom" ' + customChecked + '>'
        + '<input id="payment-email-batch-custom-input-' + i + '" class="form-control form-control-sm" type="email"'
        + ' placeholder="Enter email address" style="width:200px;" autocomplete="email">'
        + '</div></td>'
        + '<td id="payment-email-batch-status-' + i + '" style="padding:6px 8px;vertical-align:middle;">'
        + status + '</td>'
        + '</tr>';
    }

    return '<style>'
      + '.payment-email-batch-table .form-check-input{margin:0 12px 0 0 !important;float:none !important;'
      + 'position:static !important;width:16px;height:16px;}'
      + '</style>'
      + '<div style="max-height:280px;overflow:auto;border:1px solid #dee2e6;border-radius:6px;">'
      + '<table class="table table-sm mb-0 payment-email-batch-table" style="font-size:13px;">'
      + '<thead><tr>'
      + '<th style="padding:6px 8px;">Patient ({LastName}, {FirstName})</th>'
      + '<th style="padding:6px 8px;">Patient email</th>'
      + '<th style="padding:6px 8px;">Custom</th>'
      + '<th style="padding:6px 8px;">Status</th>'
      + '</tr></thead><tbody>' + rows + '</tbody></table></div>';
  }

  private sendSetupLinkEmailsSequentially(recipients: any[]): void {
    const self = this;
    const queue = (recipients || []).slice();
    let sent = 0;
    let failed = 0;
    const failures: string[] = [];

    const updateProgress = function (current: number, total: number) {
      Swal.fire({
        title: 'Sending emails...',
        html: '<p class="mb-0">Sending ' + current + ' of ' + total + '</p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        onOpen: function () {
          Swal.showLoading();
        }
      });
    };

    const finish = function () {
      self.loadPaymentSetupLinkEmailLastSent();
      let html = '<p><strong>Sent:</strong> ' + sent + '</p>'
        + '<p><strong>Failed:</strong> ' + failed + '</p>';
      if (failures.length > 0) {
        html += '<div style="text-align:left;max-height:180px;overflow:auto;"><ul class="mb-0">';
        for (let i = 0; i < failures.length; i++) {
          html += '<li>' + self.escapeHtml(failures[i]) + '</li>';
        }
        html += '</ul></div>';
      }
      Swal.fire({
        icon: failed === 0 ? 'success' : (sent > 0 ? 'warning' : 'error'),
        title: failed === 0 ? 'Emails sent' : 'Send finished with errors',
        html: html,
        confirmButtonText: 'OK'
      });
    };

    const sendNext = function (index: number) {
      if (index >= queue.length) {
        finish();
        return;
      }

      const recipient = queue[index];
      const displayName = recipient.listDisplayName
        || ((recipient.lastName || '') + ', ' + (recipient.firstName || ''));
      updateProgress(index + 1, queue.length);

      const accessKey = recipient.accessKey ? String(recipient.accessKey) : '';
      if (!accessKey) {
        failed++;
        failures.push(displayName + ': access key missing');
        sendNext(index + 1);
        return;
      }

      const mode = recipient.recipientMode ? String(recipient.recipientMode) : '';
      if (!mode) {
        failed++;
        failures.push(displayName + ': email is missing or invalid');
        sendNext(index + 1);
        return;
      }

      self.paymentLinksService.generate(accessKey).subscribe(
        function (generateResult) {
          const setupLink = generateResult && generateResult.paymentMethodSetupLink
            ? String(generateResult.paymentMethodSetupLink)
            : '';
          if (!setupLink) {
            failed++;
            failures.push(displayName + ': failed to create setup link');
            sendNext(index + 1);
            return;
          }

          self.invoiceService.sendPaymentVerifyLinkEmail({
            pharmacyId: self.sharedService.pharmacyId,
            facilityId: self.sharedService.facilityId,
            patientId: recipient.patientId,
            recipientMode: mode,
            customEmail: recipient.customEmail,
            paymentMethodSetupLink: setupLink,
            performedByUserId: self.sharedService.getUserId()
          }).subscribe(
            function (sendResult) {
              if (!sendResult || !sendResult.success) {
                failed++;
                const msg = (sendResult && sendResult.data)
                  ? String(sendResult.data)
                  : 'send failed';
                failures.push(displayName + ': ' + msg);
              } else {
                sent++;
              }
              sendNext(index + 1);
            },
            function (err) {
              failed++;
              const msg = err && err.error && err.error.data
                ? String(err.error.data)
                : 'send failed';
              failures.push(displayName + ': ' + msg);
              sendNext(index + 1);
            }
          );
        },
        function (err) {
          failed++;
          const msg = err && err.error && err.error.message
            ? String(err.error.message)
            : 'failed to create setup link';
          failures.push(displayName + ': ' + msg);
          sendNext(index + 1);
        }
      );
    };

    sendNext(0);
  }

  private buildSmsRecipientPreviewHtml(
    recipients: any[],
    isProduction: boolean,
    interactive: boolean): string {
    if (!recipients || recipients.length === 0) {
      return '<p class="text-muted mb-0">No recipients.</p>';
    }
    let rows = '';
    for (let i = 0; i < recipients.length; i++) {
      const r = recipients[i];
      const name = this.escapeHtml(r.listDisplayName || ((r.lastName || '') + ', ' + (r.firstName || '')));
      const phone1 = r.phone1
        ? this.escapeHtml(r.phone1)
        : '<span class="text-muted">(not available)</span>';
      const phone2 = r.phone2
        ? this.escapeHtml(r.phone2)
        : '<span class="text-muted">(not available)</span>';
      const status = r.canSend
        ? '<span class="text-success">Ready</span>'
        : '<span class="text-danger">' + this.escapeHtml(r.skipReason || 'Skipped') + '</span>';

      if (!interactive || !r.canSend) {
        rows += '<tr>'
          + '<td style="padding:6px 8px;vertical-align:middle;">' + name + '</td>'
          + '<td style="padding:6px 8px;vertical-align:middle;">' + phone1 + '</td>'
          + '<td style="padding:6px 8px;vertical-align:middle;">' + phone2 + '</td>'
          + '<td style="padding:6px 8px;vertical-align:middle;">'
          + '<span class="text-muted">—</span></td>'
          + '<td id="payment-sms-batch-status-' + i + '" style="padding:6px 8px;vertical-align:middle;">'
          + status + '</td>'
          + '</tr>';
        continue;
      }

      const allowPhone1 = !!isProduction && !!r.allowPhone1;
      const allowPhone2 = !!isProduction && !!r.allowPhone2;
      let defaultMode = 'custom';
      if (allowPhone1) {
        defaultMode = 'phone1';
      } else if (allowPhone2) {
        defaultMode = 'phone2';
      }
      const phone1Checked = defaultMode === 'phone1' ? 'checked' : '';
      const phone2Checked = defaultMode === 'phone2' ? 'checked' : '';
      const customChecked = defaultMode === 'custom' ? 'checked' : '';
      const phone1Disabled = allowPhone1 ? '' : 'disabled';
      const phone2Disabled = allowPhone2 ? '' : 'disabled';
      const groupName = 'payment-sms-batch-mode-' + i;

      rows += '<tr>'
        + '<td style="padding:6px 8px;vertical-align:middle;">' + name + '</td>'
        + '<td style="padding:6px 8px;vertical-align:middle;white-space:nowrap;">'
        + '<label class="mb-0 d-flex align-items-center">'
        + '<input class="form-check-input m-0" type="radio" name="' + groupName + '"'
        + ' id="payment-sms-batch-phone1-' + i + '" value="phone1" ' + phone1Checked + ' ' + phone1Disabled
        + ' style="margin-right:12px;">'
        + '<span>' + phone1 + '</span></label></td>'
        + '<td style="padding:6px 8px;vertical-align:middle;white-space:nowrap;">'
        + '<label class="mb-0 d-flex align-items-center">'
        + '<input class="form-check-input m-0" type="radio" name="' + groupName + '"'
        + ' id="payment-sms-batch-phone2-' + i + '" value="phone2" ' + phone2Checked + ' ' + phone2Disabled
        + ' style="margin-right:12px;">'
        + '<span>' + phone2 + '</span></label></td>'
        + '<td style="padding:6px 8px;vertical-align:middle;">'
        + '<div class="d-flex align-items-center">'
        + '<input class="form-check-input m-0" type="radio" name="' + groupName + '"'
        + ' id="payment-sms-batch-custom-' + i + '" value="custom" ' + customChecked
        + ' style="margin-right:12px;">'
        + '<div class="input-group input-group-sm" style="width:150px;">'
        + '<span class="input-group-text">+1</span>'
        + '<input id="payment-sms-batch-custom-input-' + i + '" class="form-control" type="tel"'
        + ' placeholder="9055551234" maxlength="20" autocomplete="tel-national">'
        + '</div></div></td>'
        + '<td id="payment-sms-batch-status-' + i + '" style="padding:6px 8px;vertical-align:middle;">'
        + status + '</td>'
        + '</tr>';
    }

    return '<style>'
      + '.payment-sms-batch-table .form-check-input{margin:0 12px 0 0 !important;float:none !important;'
      + 'position:static !important;width:16px;height:16px;}'
      + '</style>'
      + '<div style="max-height:280px;overflow:auto;border:1px solid #dee2e6;border-radius:6px;">'
      + '<table class="table table-sm mb-0 payment-sms-batch-table" style="font-size:13px;">'
      + '<thead><tr>'
      + '<th style="padding:6px 8px;">Patient ({LastName}, {FirstName})</th>'
      + '<th style="padding:6px 8px;">Phone 1</th>'
      + '<th style="padding:6px 8px;">Phone 2</th>'
      + '<th style="padding:6px 8px;">Custom</th>'
      + '<th style="padding:6px 8px;">Status</th>'
      + '</tr></thead><tbody>' + rows + '</tbody></table></div>';
  }

  private sendSetupLinkSmsSequentially(recipients: any[], smsBody: string): void {
    const self = this;
    const queue = (recipients || []).slice();
    let sent = 0;
    let failed = 0;
    const failures: string[] = [];

    const updateProgress = function (current: number, total: number) {
      Swal.fire({
        title: 'Sending SMS...',
        html: '<p class="mb-0">Sending ' + current + ' of ' + total + '</p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        onOpen: function () {
          Swal.showLoading();
        }
      });
    };

    const finish = function () {
      self.loadPaymentSetupLinkSmsLastSent();
      let html = '<p><strong>Sent:</strong> ' + sent + '</p>'
        + '<p><strong>Failed:</strong> ' + failed + '</p>';
      if (failures.length > 0) {
        html += '<div style="text-align:left;max-height:180px;overflow:auto;"><ul class="mb-0">';
        for (let i = 0; i < failures.length; i++) {
          html += '<li>' + self.escapeHtml(failures[i]) + '</li>';
        }
        html += '</ul></div>';
      }
      Swal.fire({
        icon: failed === 0 ? 'success' : (sent > 0 ? 'warning' : 'error'),
        title: failed === 0 ? 'SMS sent' : 'Send finished with errors',
        html: html,
        confirmButtonText: 'OK'
      });
    };

    const sendNext = function (index: number) {
      if (index >= queue.length) {
        finish();
        return;
      }

      const recipient = queue[index];
      const displayName = recipient.listDisplayName
        || ((recipient.lastName || '') + ', ' + (recipient.firstName || ''));
      updateProgress(index + 1, queue.length);

      const accessKey = recipient.accessKey ? String(recipient.accessKey) : '';
      if (!accessKey) {
        failed++;
        failures.push(displayName + ': access key missing');
        sendNext(index + 1);
        return;
      }

      const mode = recipient.recipientMode ? String(recipient.recipientMode) : '';
      if (!mode) {
        failed++;
        failures.push(displayName + ': phone is missing or invalid');
        sendNext(index + 1);
        return;
      }

      self.paymentLinksService.generate(accessKey).subscribe(
        function (generateResult) {
          const setupLink = generateResult && generateResult.paymentMethodSetupLink
            ? String(generateResult.paymentMethodSetupLink)
            : '';
          if (!setupLink) {
            failed++;
            failures.push(displayName + ': failed to create setup link');
            sendNext(index + 1);
            return;
          }

          self.invoiceService.sendPaymentVerifyLinkSms({
            pharmacyId: self.sharedService.pharmacyId,
            facilityId: self.sharedService.facilityId,
            patientId: recipient.patientId,
            recipientMode: mode,
            customPhone: recipient.customPhone,
            smsBody: smsBody,
            paymentMethodSetupLink: setupLink,
            performedByUserId: self.sharedService.getUserId()
          }).subscribe(
            function (sendResult) {
              if (!sendResult || !sendResult.success) {
                failed++;
                const msg = (sendResult && sendResult.data)
                  ? String(sendResult.data)
                  : 'send failed';
                failures.push(displayName + ': ' + msg);
              } else {
                sent++;
              }
              sendNext(index + 1);
            },
            function (err) {
              failed++;
              const msg = err && err.error && err.error.data
                ? String(err.error.data)
                : 'send failed';
              failures.push(displayName + ': ' + msg);
              sendNext(index + 1);
            }
          );
        },
        function (err) {
          failed++;
          const msg = err && err.error && err.error.message
            ? String(err.error.message)
            : 'failed to create setup link';
          failures.push(displayName + ': ' + msg);
          sendNext(index + 1);
        }
      );
    };

    sendNext(0);
  }

  private isValidEmail(email: string): boolean {
    if (!email) {
      return false;
    }
    const e = String(email).trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }

  private sanitizeNationalPhoneInput(phone: string): string {
    if (!phone) {
      return '';
    }
    return String(phone).replace(/[^\d\s().-]/g, '');
  }

  // Custom phone UI omits +1. Prepend +1, then validate as E.164.
  private normalizeCustomNationalPhoneToE164(nationalPhone: string): string {
    var digits = String(nationalPhone || '').replace(/\D/g, '');
    if (!digits) {
      return '';
    }
    if (digits.length === 11 && digits.charAt(0) === '1') {
      return this.normalizePhoneToE164('+' + digits);
    }
    return this.normalizePhoneToE164('+1' + digits);
  }

  // E.164: + and 8-15 digits. 10-digit NANP numbers are stored as +1XXXXXXXXXX.
  private normalizePhoneToE164(phone: string): string {
    if (!phone) {
      return '';
    }
    var raw = String(phone).trim();
    var hasPlus = raw.charAt(0) === '+';
    var digits = raw.replace(/\D/g, '');
    if (!digits) {
      return '';
    }
    if (digits.length === 11 && digits.charAt(0) === '1') {
      return '+' + digits;
    }
    if (digits.length === 10) {
      return '+1' + digits;
    }
    if (hasPlus && digits.length >= 8 && digits.length <= 15 && digits.charAt(0) !== '0') {
      return '+' + digits;
    }
    return '';
  }

  private swalEnvelopeIconHtml(): string {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">'
      + '<path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414.05 3.555zM0 4.697v7.104l5.803-3.558L0 4.697zM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586l-1.239-.757zm3.436-.586L16 11.801V4.697l-5.803 3.546z"/>'
      + '</svg>';
  }

  private swalChatIconHtml(): string {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">'
      + '<path d="M16 8c0 3.866-3.582 7-8 7a9.06 9.06 0 0 1-2.347-.306c-.584.296-1.925.864-4.181 1.234-.2.032-.293-.312-.147-.517.254-.354.451-.723.587-1.126.248-.73.06-1.551-.546-2.057C.805 10.523 0 9.318 0 8c0-3.866 3.582-7 8-7s8 3.134 8 7zM5 8a1 1 0 1 0-2 0 1 1 0 0 0 2 0zm4 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>'
      + '</svg>';
  }

  private sizeSwalCustomIcon(): void {
    var content = document.querySelector('.swal2-icon-content') as HTMLElement;
    if (!content) {
      return;
    }
    content.style.fontSize = '1em';
    content.style.display = 'flex';
    content.style.alignItems = 'center';
    content.style.justifyContent = 'center';
  }

  private escapeHtml(value: string): string {
    if (!value) {
      return '';
    }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  openProfile(patientId: number): void {
    const url = 'invoice/profile/' + this.sharedService.pharmacyId + '/'
      + this.sharedService.facilityId + '/' + patientId;
    this.router.navigate([url]);
  }

  openProfilePayments(patientId: number): void {
    const url = 'invoice/profile/' + this.sharedService.pharmacyId + '/'
      + this.sharedService.facilityId + '/' + patientId;
    this.router.navigate([url], {
      queryParams: {
        tab: 'payments',
        listTab: 'payments'
      }
    });
  }

  clearErrorMessage(): void {
    this.showErrorMessage = false;
    this.errorMessage = '';
  }
}
